
import React from 'react';
import { BlogPostContent } from '@/types/blog';
import BlogContentRenderer from './BlogContentRenderer';
import BlogFAQSection from './BlogFAQSection';
import BlogMidArticleCTA from './BlogMidArticleCTA';
import { isOutdoorCoachingContent } from '@/utils/blogContentDetection';
import BlogTOC from './BlogTOC';
import StructuredDataFAQ from '@/components/seo/StructuredDataFAQ';
import { gesundesVerhaeltnisZuGeldFAQData } from '@/data/faq/gesundes-verhaeltnis-zu-geld-faq';
import { koerperFuehrungFAQData } from '@/data/faq/koerper-fuehrung-faq';
import { kiGestuetzteWebentwicklungFAQData } from '@/data/faq/ki-gestuetzte-webentwicklung-faq';
import { kiSuchmaschinen2025FAQData } from '@/data/faq/ki-suchmaschinen-2025-faq';
import { neuroplastizitaetEpigenetikFAQData } from '@/data/faq/neuroplastizitaet-epigenetik-faq';
import { outdoorCoachingFAQData } from '@/data/faq/outdoor-coaching-faq';
import { finanzcoachingVsFinanzberatungFAQData } from '@/data/faq/finanzcoaching-vs-finanzberatung-faq';
import { digitalerHumanismusFAQ } from '@/data/blog/digitaler-humanismus-kommunikation';
import { mythosPositivesDenkenFAQData } from '@/data/faq/mythos-positives-denken-faq';
import { musikUndEmotionenFAQ } from '@/data/faq/musik-und-emotionen-faq';
import { frommHabenSeinFAQ } from '@/data/faq/fromm-haben-sein-faq';
import { etfMythosFAQData } from '@/data/faq/etf-mythos-faq';
import { betriebssystemV52FAQData } from '@/data/faq/betriebssystem-v5-2-faq';
import { fuehrung2026FAQData } from '@/data/faq/fuehrung-2026-faq';
import { mythosNischeFAQData } from '@/data/faq/mythos-nische-faq';
import { hochsensibleFuehrungFAQData } from '@/data/faq/hochsensible-fuehrung-faq';
import { souveraenitaetKomplexitaetFAQData } from '@/data/faq/souveraenitaet-komplexitaet-faq';

interface BlogContentProps {
  contentToRender: string | BlogPostContent;
  slug?: string;
}

/**
 * Main blog content component that orchestrates rendering content and optional FAQ sections
 */
const BlogContent: React.FC<BlogContentProps> = ({ contentToRender, slug }) => {
  // Enhanced console logging for debugging
  console.info('BlogContent - Content type:', typeof contentToRender);
  console.info('BlogContent - Content exists:', !!contentToRender);
  console.info('BlogContent - Slug:', slug);

  // Determine which FAQ to show based on slug
  const getFAQData = () => {
    switch (slug) {
      case 'finanzcoaching-vs-finanzberatung':
        return { 
          items: finanzcoachingVsFinanzberatungFAQData, 
          title: "Häufige Fragen zu Finanzcoaching vs. Finanzberatung",
          description: "Diese FAQ-Sektion beantwortet die wichtigsten Fragen zum Unterschied zwischen Finanzcoaching und Finanzberatung. Sollten Sie weitere Fragen haben, kontaktieren Sie mich gerne für ein unverbindliches Erstgespräch."
        };
      case 'gesundes-verhaeltnis-zu-geld':
        return { 
          items: gesundesVerhaeltnisZuGeldFAQData, 
          title: "Häufige Fragen zu einem gesunden Verhältnis zu Geld",
          description: "Diese FAQ-Sektion beantwortet die wichtigsten Fragen zur Entwicklung eines bewussten Umgangs mit Geld und Finanzen. Sollten Sie weitere Fragen haben, kontaktieren Sie mich gerne für ein unverbindliches Erstgespräch."
        };
      case 'koerper-fuehrung':
        return { 
          items: koerperFuehrungFAQData, 
          title: "Häufig gestellte Fragen",
          description: "Diese FAQ-Sektion beantwortet die wichtigsten Fragen zu Körperintelligenz, Neurobiologie und gesunder Selbstführung. Sollten Sie weitere Fragen haben, kontaktieren Sie mich gerne für ein unverbindliches Erstgespräch."
        };
      case 'ki-gestuetzte-webentwicklung':
        return { 
          items: kiGestuetzteWebentwicklungFAQData, 
          title: "Häufige Fragen zur KI-gestützten Webentwicklung",
          description: "Diese FAQ-Sektion beantwortet die wichtigsten Fragen zur KI-gestützten Webentwicklung und deren Auswirkungen. Sollten Sie weitere Fragen haben, kontaktieren Sie mich gerne für ein unverbindliches Erstgespräch."
        };
      case 'ki-suchmaschinen-2025':
        return { 
          items: kiSuchmaschinen2025FAQData, 
          title: "Häufig gestellte Fragen zu KI-Suchmaschinen",
          description: "Diese FAQ-Sektion beantwortet die wichtigsten Fragen zu KI-Suchmaschinen und ihrer Nutzung. Sollten Sie weitere Fragen haben, kontaktieren Sie mich gerne für ein unverbindliches Erstgespräch."
        };
      case 'neuroplastizitaet-epigenetik':
        return { 
          items: neuroplastizitaetEpigenetikFAQData, 
          title: "Häufige Fragen zu Neuroplastizität & Epigenetik",
          description: "Diese FAQ-Sektion beantwortet die wichtigsten Fragen zu Neuroplastizität und Epigenetik im Coaching-Kontext. Sollten Sie weitere Fragen haben, kontaktieren Sie mich gerne für ein unverbindliches Erstgespräch."
        };
      case 'outdoor-coaching':
        return { 
          items: outdoorCoachingFAQData, 
          title: "Häufige Fragen zum Outdoor-Coaching",
          description: "Diese FAQ-Sektion beantwortet die wichtigsten Fragen rund um mein integratives Outdoor-Coaching-Angebot. Sollten Sie weitere Fragen haben, kontaktieren Sie mich gerne für ein unverbindliches Erstgespräch."
        };
      case 'digitaler-humanismus-kommunikation':
        return { 
          items: digitalerHumanismusFAQ, 
          title: "Häufige Fragen",
          description: "Diese FAQ-Sektion beantwortet die wichtigsten Fragen zu digitalem Humanismus und moderner Kommunikation. Sollten Sie weitere Fragen haben, kontaktieren Sie mich gerne für ein unverbindliches Erstgespräch."
        };
      case 'mythos-positives-denken':
        return { 
          items: mythosPositivesDenkenFAQData, 
          title: "FAQ: Häufig gestellte Fragen & Einwände",
          description: "Diese FAQ-Sektion beantwortet die wichtigsten Fragen zu radikaler Akzeptanz und positivem Denken. Sollten Sie weitere Fragen haben, kontaktieren Sie mich gerne für ein unverbindliches Erstgespräch."
        };
      case 'musik-und-emotionen':
        return { 
          items: musikUndEmotionenFAQ, 
          title: "Häufig gestellte Fragen",
          description: ""
        };
      case 'fromm-haben-sein':
        return { 
          items: frommHabenSeinFAQ, 
          title: "FAQ: Häufig gestellte Fragen",
          description: ""
        };
      case 'etf-mythos':
        return { 
          items: etfMythosFAQData, 
          title: "FAQ: Häufig gestellte Fragen",
          description: "Diese FAQ-Sektion beantwortet die wichtigsten Fragen zum Thema ETFs, aktive Fonds und Behavioral Finance. Sollten Sie weitere Fragen haben, kontaktieren Sie mich gerne für ein unverbindliches Erstgespräch."
        };
      case 'betriebssystem-v5-2':
        return { 
          items: betriebssystemV52FAQData, 
          title: "FAQ: Häufig gestellte Fragen",
          description: "Diese FAQ-Sektion beantwortet die wichtigsten Fragen zu persönlichen Frameworks, Selbstmanagement und bewusster Entscheidungsfindung im Coaching-Kontext."
        };
      case 'fuehrung-2026-haltung-empathie':
        return { 
          items: fuehrung2026FAQData, 
          title: "Häufige Fragen zur Führung im Jahr 2026",
          description: ""
        };
      case 'mythos-nische':
        return { 
          items: mythosNischeFAQData, 
          title: "Häufige Fragen",
          description: ""
        };
      case 'hochsensible-fuehrung':
        return { 
          items: hochsensibleFuehrungFAQData, 
          title: "Häufige Fragen zu Hochsensibilität im Beruf",
          description: ""
        };
      case 'souveraenitaet-komplexitaet-katalysator-methode':
        return { 
          items: souveraenitaetKomplexitaetFAQData, 
          title: "Häufige Fragen zur Katalysator-Methode",
          description: ""
        };
      default:
        return null;
    }
  };

  const faqData = getFAQData();

  // Determine if we should show mid-article CTA
  const shouldShowMidArticleCTA = () => {
    // Don't show on outdoor-coaching (has custom CTA)
    if (slug === 'outdoor-coaching') return false;
    
    // Only show on longer articles (with structured content or significant length)
    if (typeof contentToRender === 'object' && contentToRender.sections) {
      return contentToRender.sections.length >= 3;
    }
    
    return true; // Show by default for string content
  };

  // Calculate position for CTA insertion (after ~60-70% of content)
  const getCTAInsertionIndex = () => {
    if (typeof contentToRender === 'object' && contentToRender.sections) {
      const sections = contentToRender.sections;
      // Insert before last section (typically conclusion/fazit)
      return sections.length - 1;
    }
    return -1; // Don't insert for string content (handled differently)
  };

  const showCTA = shouldShowMidArticleCTA();
  const ctaIndex = getCTAInsertionIndex();

  return (
    <>
      {/* Render content based on its type */}
      <div className="blog-content-wrapper relative">
        {typeof contentToRender === 'object' && contentToRender.sections ? (
          // Structured content with CTA injection
          <>
            <BlogTOC sections={contentToRender.sections.filter(s => s.title && s.title.trim() !== '')} />
            {contentToRender.sections.map((section, index) => (
              <React.Fragment key={section.id || index}>
                <BlogContentRenderer content={{ sections: [section] }} />
                {/* Insert CTA after designated section */}
                {showCTA && index === ctaIndex && <BlogMidArticleCTA />}
              </React.Fragment>
            ))}
          </>
        ) : (
          // String content
          <>
            <BlogContentRenderer content={contentToRender} />
            {/* Show CTA after string content if appropriate */}
            {showCTA && <BlogMidArticleCTA variant="minimal" />}
          </>
        )}
      </div>
      
      {/* Conditionally render FAQ section based on slug */}
      {faqData && (
        <>
          <StructuredDataFAQ faqItems={faqData.items} pageName={slug} />
          <BlogFAQSection 
            items={faqData.items}
            title={faqData.title}
            description={faqData.description}
          />
        </>
      )}
    </>
  );
};

export default BlogContent;
